import { ReactNode } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import * as RadixSelect from '@radix-ui/react-select';
import * as RadixSlider from '@radix-ui/react-slider';
import * as RadixPopover from '@radix-ui/react-popover';
import * as RadixSwitch from '@radix-ui/react-switch';
import { Check, ChevronDown } from 'lucide-react';

/**
 * Behavioural primitives.
 *
 * These wrap Radix rather than the hand-rolled equivalents for the parts that
 * are genuinely hard to get right: focus management and restore, escape and
 * outside-click dismissal, typeahead and arrow-key navigation in menus, ARIA
 * wiring, and collision-aware positioning so a popover near the edge of the
 * window flips instead of being clipped. The look stays ours — every part is
 * styled with the same tokens as the rest of the editor.
 */

/**
 * A tooltip that can hold a sentence.
 *
 * The native `title` attribute is slow to appear, cannot be styled, and
 * vanishes while reading; several controls here need a line of explanation
 * rather than a label.
 */
export function Tip({
  children,
  label,
  side = 'bottom',
  delay = 420,
}: {
  children: ReactNode;
  label: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  delay?: number;
}): JSX.Element {
  if (!label) return <>{children}</>;
  return (
    <RadixTooltip.Root delayDuration={delay}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content className="tip" side={side} sideOffset={6} collisionPadding={8}>
          {label}
          <RadixTooltip.Arrow className="tip-arrow" width={10} height={5} />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
}

/** Wraps the app once so tooltips share one timing controller. */
export function TooltipLayer({ children }: { children: ReactNode }): JSX.Element {
  return (
    <RadixTooltip.Provider delayDuration={420} skipDelayDuration={300}>
      {children}
    </RadixTooltip.Provider>
  );
}

export interface SelectOption<T extends string> {
  value: T;
  label: string;
  hint?: string;
  group?: string;
}

/**
 * A select that behaves like one.
 *
 * A native `<select>` renders its list with the operating system's own
 * styling, which in a dark editor means a white popup; it also cannot show a
 * second line of detail per option.
 */
export function Select<T extends string>({
  value,
  options,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: T;
  options: ReadonlyArray<SelectOption<T>>;
  onChange: (value: T) => void;
  placeholder?: string;
  ariaLabel?: string;
}): JSX.Element {
  const groups = [...new Set(options.map((o) => o.group ?? ''))];

  return (
    <RadixSelect.Root value={value} onValueChange={(v) => onChange(v as T)}>
      <RadixSelect.Trigger className="select-trigger" aria-label={ariaLabel}>
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon>
          <ChevronDown size={13} />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content className="select-content" position="popper" sideOffset={4} collisionPadding={8}>
          <RadixSelect.Viewport className="select-viewport">
            {groups.map((group) => (
              <RadixSelect.Group key={group}>
                {group ? <RadixSelect.Label className="select-label">{group}</RadixSelect.Label> : null}
                {options
                  .filter((o) => (o.group ?? '') === group)
                  .map((option) => (
                    <RadixSelect.Item key={option.value} value={option.value} className="select-item">
                      <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                      {option.hint ? <span className="select-hint">{option.hint}</span> : null}
                      <RadixSelect.ItemIndicator className="select-check">
                        <Check size={12} />
                      </RadixSelect.ItemIndicator>
                    </RadixSelect.Item>
                  ))}
              </RadixSelect.Group>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

/** A slider with a real keyboard story: arrows, Home, End, Page Up/Down. */
export function Slider({
  value,
  min,
  max,
  step = 1,
  onChange,
  ariaLabel,
}: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  ariaLabel?: string;
}): JSX.Element {
  return (
    <RadixSlider.Root
      className="slider"
      value={[value]}
      min={min}
      max={max}
      step={step}
      onValueChange={([v]) => onChange(v)}
      aria-label={ariaLabel}
    >
      <RadixSlider.Track className="slider-track">
        <RadixSlider.Range className="slider-range" />
      </RadixSlider.Track>
      <RadixSlider.Thumb className="slider-thumb" />
    </RadixSlider.Root>
  );
}

export function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}): JSX.Element {
  return (
    <label className="switch-row">
      <RadixSwitch.Root className="switch" checked={checked} onCheckedChange={onChange}>
        <RadixSwitch.Thumb className="switch-thumb" />
      </RadixSwitch.Root>
      <span>{label}</span>
    </label>
  );
}

/** A popover that stays on screen near the window edge. */
export function Popover({
  trigger,
  children,
  side = 'bottom',
  align = 'start',
}: {
  trigger: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}): JSX.Element {
  return (
    <RadixPopover.Root>
      <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
      <RadixPopover.Portal>
        <RadixPopover.Content
          className="popover"
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={10}
        >
          {children}
          <RadixPopover.Arrow className="popover-arrow" width={11} height={5} />
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}
